import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { FirebasePages } from '../../utilities/firebaseUtils';
import { axiosBaseQuery } from '../../utilities/rtk-query';
import { FindLoadsTableData } from '../findLoads/findLoadsTypes';
import { BookingDetailsStatusLiteral, QuoteSchema } from './quoteTypes';

const quotingApi = createApi({
  reducerPath: 'quotingApi',
  baseQuery: axiosBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_QUOTE_URL!,
  }),
  tagTypes: [],
  endpoints: (builder) => ({
    bookItNowQuote: builder.mutation<
      QuoteSchema,
      { orderId: string; amount: number }
    >({
      query: ({ orderId, amount }) => {
        return {
          url: '/quotes/book-it-now',
          method: 'POST',
          data: { orderId, amount },
        };
      },
    }),
    cancelBINQuote: builder.mutation<
      QuoteSchema,
      { quoteId: string; status: BookingDetailsStatusLiteral }
    >({
      query: ({ quoteId, status }) => {
        return {
          url: `/quotes/${quoteId}/book`,
          method: 'PATCH',
          data: { status },
        };
      },
    }),
    confirmBookItNowQuote: builder.mutation<
      QuoteSchema,
      { quoteId: string; details: BookItNowConfirmFormData }
    >({
      query: ({ quoteId, details }) => {
        return {
          url: `/quotes/${quoteId}/booking`,
          method: 'POST',
          data: { ...details },
        };
      },
    }),
  }),
});

// This Quote State should be replace when the GetQuote endpoint migrates to be here in the quote API
interface QuoteState {
  binQuote: QuoteSchema | null;
}

const initialState: QuoteState = {
  binQuote: null,
};

export const quoteSlice = createSlice({
  name: 'quote',
  initialState,
  reducers: {
    setQuote(state, action: PayloadAction<QuoteSchema>) {
      return { ...state, binQuote: action.payload };
    },
    clearQuote(state) {
      state.binQuote = null;
    },
  },
});

interface BookItNowConfirmFormData {
  truckNumber: string | undefined;
  trailerNumber: string | undefined;
  trailerType: string | undefined;
  driverName: string | undefined;
  driverCellPhone: string | undefined;
  emptyLocation: string | undefined;
  emptyDate: string | undefined;
  pickupETA: string | undefined;
  mcNumber: string | undefined;
  contactEmail: string | undefined;
  deadheadMiles?: number;
}

export interface FirebaseData {
  firebasePage:
    | FirebasePages.DETAILS
    | FirebasePages.LIST
    | FirebasePages.MY_LANES
    | FirebasePages.MY_QUOTES
    | undefined;
  firebaseDescriptionBinNextClick: string | undefined;
}

interface BookItNowConfirmDialogState {
  isOpen: boolean;
  orderNumber: string;
  loadData: FindLoadsTableData | undefined;
  confirmFormData: BookItNowConfirmFormData;
  timeZoneIdentifier: string | undefined;
  fromQuoteDrawer: boolean;
  firebaseData: FirebaseData;
}

const initialBookItNowConfirmDialogState: BookItNowConfirmDialogState = {
  isOpen: false,
  orderNumber: '',
  loadData: undefined,
  confirmFormData: {
    truckNumber: undefined,
    trailerNumber: undefined,
    trailerType: undefined,
    driverName: undefined,
    driverCellPhone: undefined,
    emptyLocation: undefined,
    emptyDate: undefined,
    pickupETA: undefined,
    mcNumber: undefined,
    contactEmail: undefined,
  },
  timeZoneIdentifier: undefined,
  fromQuoteDrawer: false,
  firebaseData: {
    firebasePage: undefined,
    firebaseDescriptionBinNextClick: undefined,
  },
};

export const automatedBINSlice = createSlice({
  name: 'bookItNowConfirmDialog',
  initialState: initialBookItNowConfirmDialogState,
  reducers: {
    showBookItNowConfirmDialog: (
      state,
      action: PayloadAction<{
        show: boolean;
        orderNumber: string;
        loadData?: FindLoadsTableData;
        timeZoneIdentifier: string | undefined;
        fromQuoteDrawer: boolean;
        firebaseData: FirebaseData;
      }>
    ) => {
      state.isOpen = action.payload.show;
      state.orderNumber = action.payload.orderNumber;
      state.loadData = action.payload.loadData;
      state.timeZoneIdentifier = action.payload.timeZoneIdentifier;
      state.fromQuoteDrawer = action.payload.fromQuoteDrawer;
      state.firebaseData = action.payload.firebaseData;
      state.confirmFormData =
        initialBookItNowConfirmDialogState.confirmFormData; // reset the confirmFormData
    },
    setConfirmFormData: (
      state,
      action: PayloadAction<BookItNowConfirmDialogState['confirmFormData']>
    ) => {
      // We don't want to overwrite deadheadMiles since it is set before the user submits the rest of the form data
      state.confirmFormData = {
        ...action.payload,
        deadheadMiles: state.confirmFormData.deadheadMiles,
      };
    },
    setConfirmFormDataDeadheadMiles: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      if (action.payload === undefined) {
        delete state.confirmFormData.deadheadMiles;
      } else {
        state.confirmFormData.deadheadMiles = action.payload;
      }
    },
  },
});
export const {
  showBookItNowConfirmDialog,
  setConfirmFormData,
  setConfirmFormDataDeadheadMiles,
} = automatedBINSlice.actions;

// This Quote State should be replace when the GetQuote endpoint migrates to be here in the quote API

export const {
  useBookItNowQuoteMutation,
  useCancelBINQuoteMutation,
  useConfirmBookItNowQuoteMutation,
} = quotingApi;

export const { setQuote, clearQuote } = quoteSlice.actions;

export default quotingApi;
